import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { it } from "date-fns/locale";
import { CalendarDays } from "lucide-react";

const toText = (iso) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split("-").reverse().join("/") : "");
const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function parse(text, allowFuture) {
  const m = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return d.getDate() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 && (allowFuture || d <= new Date()) ? d : null;
}

function mask(v, prev = "") {
  const deleting = v.length < prev.length;
  let d = v.replace(/\D/g, "").slice(0, 8);
  if (deleting && prev.endsWith("/") && v === prev.slice(0, -1)) d = d.slice(0, -1);
  const out = d.slice(0, 2) + (d.length >= 2 ? "/" : "") + d.slice(2, 4) + (d.length >= 4 ? "/" : "") + d.slice(4);
  return deleting && out.endsWith("/") ? out.slice(0, -1) : out;
}

const message = (text, blurred, allowFuture) => {
  if (text.length === 10 && !parse(text, allowFuture)) return allowFuture ? "Data non valida" : "Data non valida o nel futuro";
  return blurred && text.length > 0 && text.length < 10 ? "Completa la data: gg/mm/aaaa" : "";
};

export function DateInputIt({ value, onChange, testid, fromYear = 1920, allowFuture = false, error = "", disabled = false, className = "" }) {
  const [text, setText] = useState(toText(value));
  const [open, setOpen] = useState(false);
  const [blurred, setBlurred] = useState(false);
  useEffect(() => { setText((t) => (value ? toText(value) : parse(t, allowFuture) ? "" : t)); }, [value, allowFuture]);
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  const now = new Date();
  const onText = (e) => {
    const t = mask(e.target.value, text);
    setText(t);
    const d = parse(t, allowFuture);
    onChange(d ? toIso(d) : "");
  };
  const msg = message(text, blurred, allowFuture) || error;
  return (
    <div className={className}>
      <div className="flex gap-2">
        <Input inputMode="numeric" placeholder="gg/mm/aaaa" value={text} disabled={disabled} onChange={onText} onBlur={() => setBlurred(true)} aria-invalid={!!msg} data-testid={testid} className={msg ? "border-destructive" : ""} />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="icon" disabled={disabled} aria-label="Apri calendario" data-testid={`${testid}-calendar-btn`}><CalendarDays className="w-4 h-4" /></Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar mode="single" locale={it} captionLayout="dropdown-buttons" fromYear={fromYear} toYear={now.getFullYear() + (allowFuture ? 20 : 0)}
              classNames={{ caption_label: "hidden", caption_dropdowns: "flex gap-1", vhidden: "sr-only", dropdown: "bg-background border border-border rounded px-1 py-0.5 text-sm", dropdown_month: "", dropdown_year: "" }}
              defaultMonth={selected || (allowFuture ? now : new Date(1980, 0, 1))} selected={selected} disabled={allowFuture ? undefined : { after: now }}
              onSelect={(d) => { if (d) { onChange(toIso(d)); setOpen(false); } }} data-testid={`${testid}-calendar`} />
          </PopoverContent>
        </Popover>
      </div>
      {msg && <p className="text-xs text-destructive mt-1" role="alert" data-testid={`${testid}-error`}>{msg}</p>}
    </div>
  );
}
